import React, { useEffect, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { getVision } from '../../actions/planningAction';
import PlanningDashboard from './PlanningDashboard';
import PlanningChat from './PlanningChat';
import PlanningJournal from './PlanningJournal';
import PlanningExecute from './PlanningExecute';
import PlanningLibrary from './PlanningLibrary';
import PlanningHabits from './PlanningHabits';
import { useTheme } from '../../context/ThemeContext';
import './PlanningWorkspace.css';

export default function PlanningWorkspace() {
  const { visionId } = useParams();
  const dispatch = useDispatch();
  const { activeVision, milestones, loading } = useSelector(s => s.planning);
  const { theme } = useTheme();
  const [showChat,      setShowChat]      = useState(false);
  const [view,          setView]          = useState('dashboard');
  const [focusNode,     setFocusNode]     = useState(null);
  const [chatFocusNode, setChatFocusNode] = useState(null);

  const openChatWithNode = useCallback((node) => {
    setChatFocusNode({ ...node, ts: Date.now() });
    setShowChat(true);
  }, []);

  const refresh = useCallback(() => {
    return dispatch(getVision(visionId));
  }, [visionId, dispatch]);

  useEffect(() => {
    refresh();
  }, [visionId]);


  if (loading && !activeVision) {
    return <div className="pw-loading">Loading...</div>;
  }
  if (!activeVision) {
    return <div className="pw-loading">Vision not found.</div>;
  }

  return (
    <div className={`pw-page pw-page--${theme}`}>
      {/* Nav */}
      <div className="pw-nav">
        <div className="pw-view-toggle">
          <button
            className={`pw-view-btn${view === 'dashboard' ? ' pw-view-btn--active' : ''}`}
            onClick={() => setView('dashboard')}
          >
            ☰ Plan
          </button>
          <button
            className={`pw-view-btn${view === 'execute' ? ' pw-view-btn--active' : ''}`}
            onClick={() => setView('execute')}
          >
            ▶ Execute
          </button>
          <button
            className={`pw-view-btn${view === 'habits' ? ' pw-view-btn--active' : ''}`}
            onClick={() => setView('habits')}
          >
            ↻ Habits
          </button>
          <button
            className={`pw-view-btn${view === 'library' ? ' pw-view-btn--active' : ''}`}
            onClick={() => setView('library')}
          >
            ◆ Library
          </button>
          <button
            className={`pw-view-btn${view === 'journal' ? ' pw-view-btn--active' : ''}`}
            onClick={() => setView('journal')}
          >
            ✦ Journal
          </button>
        </div>

        <div className="pw-nav-right">
          <button className="pw-chat-toggle" onClick={() => setShowChat(s => !s)}>
            {showChat ? 'Close Chat' : '✦ Chat'}
          </button>
        </div>
      </div>

      <div className="pw-content">
        {view === 'dashboard' && (
          <PlanningDashboard
            vision={activeVision}
            milestones={milestones}
            visionId={visionId}
            onRefresh={refresh}
            theme={theme}
            focusNode={focusNode}
            onOpenChatWithNode={openChatWithNode}
          />
        )}
        {view === 'execute' && (
          <PlanningExecute
            vision={activeVision}
            milestones={milestones}
            visionId={visionId}
            onRefresh={refresh}
            onFocusNode={(id) => { setFocusNode({ id, ts: Date.now() }); setView('dashboard'); }}
            theme={theme}
          />
        )}
        {view === 'habits' && (
          <PlanningHabits
            visionId={visionId}
            milestones={milestones}
            theme={theme}
            onFocusNode={(id) => { setFocusNode({ id, ts: Date.now() }); setView('dashboard'); }}
            onGoToPlan={() => setView('dashboard')}
          />
        )}
        {view === 'library' && (
          <PlanningLibrary
            visionId={visionId}
            milestones={milestones}
            theme={theme}
          />
        )}
        {view === 'journal' && (
          <PlanningJournal
            vision={activeVision}
            visionId={visionId}
            theme={theme}
          />
        )}

        {showChat && (
          <div className="pw-chat-drawer">
            <PlanningChat visionId={visionId} milestones={milestones} onRefresh={refresh} onFocusNode={(id) => { setFocusNode({ id, ts: Date.now() }); setView('dashboard'); }} initialAttachedNode={chatFocusNode} theme={theme} />
          </div>
        )}
      </div>
    </div>
  );
}
